<script setup>
import { onBeforeUnmount, ref, watch } from "vue";
import apiClient from "@/apiClient";

const props = defineProps({ referenceTypeId: { type: Number, required: true } });
const emit = defineEmits(["apply"]);
const emailText = ref("");
const busy = ref(false);
const error = ref("");
const result = ref(null);
let timer = null;
let generation = 0;
const unwrap = (response) => response?.data?.data ?? response?.data;
const reset = () => {
  generation += 1;
  clearTimeout(timer);
  busy.value = false;
  result.value = null;
  error.value = "";
};
onBeforeUnmount(reset);
watch(() => props.referenceTypeId, reset);
watch(emailText, reset);

const extract = async () => {
  reset();
  if (emailText.value.trim().length < 20) {
    error.value = "Paste the enquiry email (at least 20 characters).";
    return;
  }
  busy.value = true;
  const current = generation;
  const fail = (err) => {
    if (current !== generation) return;
    busy.value = false;
    error.value = err.response?.data?.message ?? err.message ?? "Unable to extract this email.";
  };
  const poll = async (id) => {
    try {
      const response = await apiClient.get(`/api/quote/extract-draft/${id}`, { suppressAutoReload: true, suppressErrorAlert: true });
      if (current !== generation) return;
      const payload = unwrap(response);
      if (payload?.status === "completed") {
        if (!payload.draft?.versions?.[0]?.pts_risk) throw new Error("No patient transport details returned.");
        result.value = payload;
        busy.value = false;
      } else if (payload?.status === "failed") {
        throw new Error(payload.error || "Unable to extract this email.");
      } else if (payload?.status === "queued" || payload?.status === "processing") {
        timer = setTimeout(() => poll(id), 2000);
      } else {
        throw new Error("Unable to check extraction progress. Try again.");
      }
    } catch (err) { fail(err); }
  };
  try {
    const response = await apiClient.post("/api/quote/extract-draft", {
      reference_type_id: props.referenceTypeId,
      email_text: emailText.value,
    }, { suppressAutoReload: true, suppressErrorAlert: true });
    if (current !== generation) return;
    const payload = unwrap(response);
    if (!payload?.id) throw new Error("Extraction did not start. Try again.");
    await poll(payload.id);
  } catch (err) { fail(err); }
};
const apply = () => {
  if (!result.value) return;
  emit("apply", result.value);
  reset();
};
</script>

<template>
  <FormBox header="AI Extraction From Email">
    <div class="space-y-3">
      <p class="text-sm text-slate-500">Paste a transport enquiry to detect patient, pickup, destination, timing and transport requirements. Review the suggestions before applying them.</p>
      <textarea v-model="emailText" aria-label="Transport enquiry email" class="w-full rounded border p-2 dark:bg-slate-800" rows="6" placeholder="Paste the enquiry email here, including sender details." :disabled="busy"></textarea>
      <button type="button" class="btn accent" :disabled="busy" @click="extract">{{ busy ? "Extracting…" : "Extract Email" }}</button>
      <p v-if="error" role="alert" class="text-sm text-rose-500">{{ error }}</p>
      <div v-if="result" class="space-y-3 rounded border border-emerald-200 p-3">
        <p class="text-sm font-medium">{{ result.draft.description }}</p>
        <p v-if="result.matched_customer || result.suggested_customer" class="text-sm">Customer: {{ result.matched_customer?.company || result.suggested_customer?.company || 'Not identified' }}</p>
        <p v-if="result.matched_recipient_contact || result.suggested_recipient" class="text-sm">Requester: {{ result.matched_recipient_contact?.name || result.suggested_recipient?.name }} {{ result.matched_recipient_contact?.email || result.suggested_recipient?.email }}</p>
        <p v-if="result.draft.versions[0].dates?.[0]?.resources?.length" class="text-sm">Suggested transport units: {{ result.draft.versions[0].dates[0].resources.map(resource => `${resource.quantity} × ${resource.name || resource.product?.name || `Unit #${resource.product_id}`}`).join(', ') }}</p>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 text-sm">
          <div v-for="(value, field) in result.draft.versions[0].pts_risk" :key="field">
            <template v-if="!['extracted_at', 'missing_fields'].includes(field)">
              <span class="font-medium capitalize">{{ field.replaceAll('_', ' ') }}:</span>
              {{ typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value }}
            </template>
          </div>
        </div>
        <p v-if="result.questions?.length" class="text-sm text-amber-700">Missing information: {{ result.questions.join(' • ') }}</p>
        <button type="button" class="btn accent" @click="apply">Apply to Quote</button>
        <p class="text-xs text-slate-500">Review the populated fields and save the quote to calculate journey times and pricing.</p>
      </div>
    </div>
  </FormBox>
</template>
